import { openBulkEditModal } from '@app/features/entity/bulk-edit/BulkEditEntityModal';
import {
  makeAddTagAction,
  makeCopyEntityIdAction,
  makeCopyLinkAction,
  makeCreateReminderAction,
  makeFavoriteAction,
  makeMuteAction,
} from '@app/features/next-soup/actions';
import { ProjectAssignmentDialog } from '@app/features/projects/projects';
import { useFeatureFlag } from '@app/lib/analytics/posthog';
import { useGlobalNotificationSource } from '@components/app/GlobalAppState';
import { MobileDrawer } from '@components/app/mobile/MobileDrawer';
import type { BlockTool } from '@components/app/ResponsiveBlockToolbar';
import {
  type BlockAlias,
  type BlockName,
  useBlockAliasedName,
} from '@core/block';
import { useItemOperations } from '@core/component/FileList/useItemOperations';
import { Permissions } from '@core/component/SharePermissions';
import { toast } from '@core/component/Toast/Toast';
import { resolveBlockAlias } from '@core/constant/allBlocks';
import { enableProjects, enableReminders } from '@core/constant/featureFlags';
import { useQuickAccess } from '@core/context/quickAccess';
import { triggerFocusInput } from '@core/directive/focusInput';
import { type HotkeyToken, TOKENS } from '@core/hotkey/tokens';
import { getActiveCommandByToken } from '@core/hotkey/utils';
import { isTouchDevice } from '@core/mobile/isTouchDevice';
import { useGetPermissions } from '@core/signal/permissions';
import { buildEntityData, type EntityData } from '@entity';
import ArrowRight from '@phosphor/arrow-right.svg';
import BellSimple from '@phosphor/bell-simple.svg';
import BellSlash from '@phosphor/bell-slash.svg';
import CaretDown from '@phosphor/caret-down.svg';
import CaretRight from '@phosphor/caret-right.svg';
import Check from '@phosphor/check.svg';
import Copy from '@phosphor/copy.svg';
import DotsThree from '@phosphor/dots-three.svg';
import Link from '@phosphor/link.svg';
import Rename from '@phosphor/pencil-line.svg';
import Stack from '@phosphor/stack.svg';
import Star from '@phosphor/star.svg';
import Tag from '@phosphor/tag.svg';
import Trash from '@phosphor/trash-simple.svg';
import type { ItemType } from '@service-storage/itemType';
import { cn, Dropdown, Hotkey } from '@ui';
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  onCleanup,
  Show,
  useContext,
} from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { match } from 'ts-pattern';
import {
  getSplitFileMenuActionSections,
  type SplitFileMenuAction,
  type SplitFileMenuActionGroup,
  type SplitFileMenuActionGroups,
  SplitPanelContext,
} from '../context';
import { useSplitLayout } from '../layout';
import { returnSplitToRecentListView } from '../layoutUtils';

export type FileOperationName = 'delete' | 'rename' | 'copy' | 'moveToProject';

export type DefaultFileOperation = {
  op: FileOperationName;
};

export type CustomFileOperation = {
  label: string;
  icon: Component;
  action?: () => void;
  children?: SplitFileMenuAction[];
  group?: SplitFileMenuActionGroup;
};

const isDefaultFileOperation = (
  op: FileOperation
): op is DefaultFileOperation => {
  return 'op' in op;
};

export type FileOperation = DefaultFileOperation | CustomFileOperation;

/**
 * A titled radio group leading the mobile drawer, mirroring which view (page)
 * of the block is open — for blocks whose inline view tabs have no room on
 * mobile (e.g. the channel's Messages/Attachments/Participants tabs).
 */
export type SplitFileMenuViews = {
  /** Section title shown above the radio group, e.g. 'View'. */
  title: string;
  options: Array<{
    value: string;
    label: string | JSX.Element;
    icon: Component;
  }>;
  value: string;
  onSelect: (value: string) => void;
};

function SplitMenuItemContent(
  props: Pick<SplitFileMenuAction, 'hotkeyToken' | 'icon' | 'label'> & {
    showHotkey?: boolean;
  }
) {
  return (
    <>
      <Dynamic
        component={props.icon as Component<JSX.SvgSVGAttributes<SVGSVGElement>>}
        class="size-4 shrink-0"
      />
      <div class="flex-1 truncate">{props.label}</div>
      <Show when={props.showHotkey !== false && props.hotkeyToken}>
        <Hotkey
          token={props.hotkeyToken}
          class="ml-4 shrink-0"
          theme="subtle"
          showPlus
        />
      </Show>
    </>
  );
}

type SplitFileMenuRenderProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerClass?: string;
  groups: SplitFileMenuActionGroups;
};

function DesktopRender(props: SplitFileMenuRenderProps) {
  const sections = () => getSplitFileMenuActionSections(props.groups);

  const item = (action: SplitFileMenuAction) => {
    const children = () => action.children?.filter(Boolean) ?? [];

    return (
      <Show
        when={children().length > 0}
        fallback={
          <Dropdown.Item
            onSelect={() => {
              action.action?.();
              props.onOpenChange(false);
            }}
          >
            <SplitMenuItemContent {...action} />
          </Dropdown.Item>
        }
      >
        <Dropdown.Sub>
          <Dropdown.SubTrigger>
            <SplitMenuItemContent {...action} />
            <CaretRight class="size-3.5 shrink-0" />
          </Dropdown.SubTrigger>
          <Dropdown.SubContent>
            <Dropdown.Group>
              <For each={children()}>{item}</For>
            </Dropdown.Group>
          </Dropdown.SubContent>
        </Dropdown.Sub>
      </Show>
    );
  };

  return (
    <Dropdown open={props.open} onOpenChange={props.onOpenChange}>
      <Dropdown.Trigger
        class={cn(props.triggerClass)}
        size="icon-sm"
        variant="ghost"
      >
        <DotsThree />
      </Dropdown.Trigger>
      <Dropdown.Content class="w-64">
        <For each={sections()}>
          {(section) => (
            <Dropdown.Group>
              <For each={section.actions}>{item}</For>
            </Dropdown.Group>
          )}
        </For>
      </Dropdown.Content>
    </Dropdown>
  );
}

function MobileRender(
  props: SplitFileMenuRenderProps & { views?: SplitFileMenuViews }
) {
  const [expandedSubmenu, setExpandedSubmenu] =
    createSignal<SplitFileMenuAction>();
  const sections = () => getSplitFileMenuActionSections(props.groups);

  const item = (action: SplitFileMenuAction, nested = false) => {
    const children = () => action.children?.filter(Boolean) ?? [];
    const expanded = () => expandedSubmenu() === action;

    return (
      <Show
        when={children().length > 0}
        fallback={
          <MobileDrawer.Item
            type="button"
            class={cn(nested ? 'pl-9 pr-4' : 'px-4')}
            onClick={(e) => {
              action.action?.(e);
              props.onOpenChange(false);
            }}
          >
            <SplitMenuItemContent {...action} showHotkey={false} />
          </MobileDrawer.Item>
        }
      >
        <div class="w-full">
          <MobileDrawer.Item
            type="button"
            class={cn(nested ? 'pl-9 pr-4' : 'px-4')}
            onClick={() => {
              setExpandedSubmenu(expanded() ? undefined : action);
            }}
          >
            <SplitMenuItemContent {...action} showHotkey={false} />
            <Dynamic
              component={expanded() ? CaretDown : CaretRight}
              class="size-3.5 shrink-0"
            />
          </MobileDrawer.Item>
          <Show when={expanded()}>
            <div class="pt-1">
              <For each={children()}>{(child) => item(child, true)}</For>
            </div>
          </Show>
        </div>
      </Show>
    );
  };

  return (
    <MobileDrawer
      side="bottom"
      open={props.open}
      onOpenChange={props.onOpenChange}
      preventScroll={false}
      preventScrollbarShift={false}
    >
      <MobileDrawer.Portal>
        <MobileDrawer.Overlay />
        <MobileDrawer.Content aria-label="File actions">
          <MobileDrawer.Handle />
          <MobileDrawer.ScrollBody>
            <Show when={props.views}>
              {(views) => (
                <>
                  <MobileDrawer.Label id="split-file-menu-views-label">
                    {views().title}
                  </MobileDrawer.Label>
                  <MobileDrawer.Section
                    role="radiogroup"
                    aria-labelledby="split-file-menu-views-label"
                    class="flex flex-col shrink-0 mb-3"
                  >
                    <For each={views().options}>
                      {(option) => (
                        <MobileDrawer.Item
                          type="button"
                          role="radio"
                          aria-checked={views().value === option.value}
                          onClick={() => {
                            views().onSelect(option.value);
                            props.onOpenChange(false);
                          }}
                        >
                          <SplitMenuItemContent
                            icon={option.icon}
                            label={option.label}
                            showHotkey={false}
                          />
                          <Show when={views().value === option.value}>
                            <Check class="size-3.5 text-accent shrink-0" />
                          </Show>
                        </MobileDrawer.Item>
                      )}
                    </For>
                  </MobileDrawer.Section>
                  {/* With a views group leading the drawer, title the action
                      sections to set the two apart. */}
                  <Show when={sections().length > 0}>
                    <MobileDrawer.Label>Actions</MobileDrawer.Label>
                  </Show>
                </>
              )}
            </Show>
            <For each={sections()}>
              {(section, index) => (
                <>
                  <Show when={index() > 0}>
                    <div class="mt-3" />
                  </Show>
                  <MobileDrawer.Section class="flex flex-col shrink-0">
                    <For each={section.actions}>{(action) => item(action)}</For>
                  </MobileDrawer.Section>
                </>
              )}
            </For>
          </MobileDrawer.ScrollBody>
        </MobileDrawer.Content>
      </MobileDrawer.Portal>
    </MobileDrawer>
  );
}

export type SplitFileMenuProps = {
  onEmailReminderSaved?: () => void | Promise<void>;
  id: string;
  itemType: ItemType;
  name: string;
  formattedName?: string;
  ops: Array<FileOperation>;
  tools?: BlockTool[];
  /** See {@link SplitFileMenuViews}. Only the mobile drawer renders it. */
  mobileViews?: SplitFileMenuViews;
  /**
   * Full entity for the menu's entity-gated items. Supply it when the block
   * can build one that generic chrome can't reconstruct from id/name/entityKind
   * alone (e.g. calls need their channelId).
   */
  entity?: EntityData;
  buttonClass?: string;
  entityKind: BlockName | BlockAlias;
  permissions: Permissions;
  onDuplicate?: (id: string) => void;
  onDelete?: () => void;
};

export function SplitFileMenu(props: SplitFileMenuProps) {
  const ctx = useContext(SplitPanelContext);
  if (!ctx)
    throw new Error('<SplitFileMenu> must be used in <SplitPanelContext>');

  const blockName = resolveBlockAlias(props.entityKind);

  const [open, setOpen] = createSignal(false);
  const [assigningProject, setAssigningProject] = createSignal(false);
  const projectsFlag = useFeatureFlag(enableProjects);
  const itemOperations = useItemOperations();
  const quickAccess = useQuickAccess();
  const favoriteAction = makeFavoriteAction();
  const notificationSource = useGlobalNotificationSource();
  const muteAction = makeMuteAction({
    notificationSource: () => notificationSource,
  });
  const createReminderAction = makeCreateReminderAction({
    onEmailSaved: props.onEmailReminderSaved,
  });
  const addTagAction = makeAddTagAction();
  const copyLinkAction = makeCopyLinkAction();
  const copyEntityIdAction = makeCopyEntityIdAction();
  // Read shortcut availability from row getters so focus changes update only
  // the hint, without recreating the action groups and losing menu focus.
  const activeHotkeyToken = (token: HotkeyToken): HotkeyToken | undefined => {
    return getActiveCommandByToken(token) ? token : undefined;
  };

  const { replaceOrInsertSplit } = useSplitLayout();

  // The entity this menu operates on: the block's own entity when supplied,
  // else the quick-access cache (richer data, covers channels/calls), else
  // built from the block's id/name/entityKind like the rename/move ops do.
  const menuEntity = createMemo<EntityData | undefined>(() => {
    if (props.entity) return props.entity;
    const item = quickAccess.getById(props.id);
    if (item?.kind === 'entity') return item.data;
    return buildEntityData({
      id: props.id,
      name: props.name,
      blockName: props.entityKind,
    });
  });

  const favoriteOp = (): SplitFileMenuAction | undefined => {
    const entity = menuEntity();
    if (!entity || !favoriteAction.canExecute(entity)) return undefined;
    return {
      label: favoriteAction.isFavorited(entity) ? 'Unfavorite' : 'Favorite',
      icon: Star,
      action: () => {
        void favoriteAction.execute([entity]);
      },
      get hotkeyToken() {
        return activeHotkeyToken(TOKENS.entity.action.favorite);
      },
      group: 'macro' as const,
    };
  };

  const muteOp = (): SplitFileMenuAction | undefined => {
    const entity = menuEntity();
    if (!entity || !muteAction.canExecute(entity)) return undefined;
    const muted = muteAction.isMuted(entity);
    return {
      label: muted ? 'Unmute notifications' : 'Mute notifications',
      icon: muted ? BellSimple : BellSlash,
      action: () => {
        void muteAction.execute([entity]);
      },
      get hotkeyToken() {
        return activeHotkeyToken(TOKENS.entity.action.mute);
      },
      group: 'macro' as const,
    };
  };

  // Read reactively as well as through the action's imperative gate: `ops` below
  // is a memo, so without a reactive dependency the item would stay missing for
  // the life of this menu if PostHog answered after it was first computed. The
  // other reminder surfaces re-evaluate per interaction and don't need this.
  const remindersFlag = useFeatureFlag(enableReminders);

  // Offer the shared snooze command for email conversations.
  const reminderOp = (): SplitFileMenuAction | undefined => {
    if (!remindersFlag().enabled) return undefined;
    const entity = menuEntity();
    if (!entity || !createReminderAction.canExecute(entity)) return undefined;
    return {
      label: 'Remind me',
      icon: BellSimple,
      action: () => {
        setOpen(false);
        createReminderAction.execute([entity]);
      },
      get hotkeyToken() {
        return activeHotkeyToken(TOKENS.entity.action.createReminder);
      },
      group: 'macro' as const,
    };
  };

  // Only offered when the block's entity type can be tagged.
  const addTagOp = (): SplitFileMenuAction | undefined => {
    const entity = menuEntity();
    if (!entity || !addTagAction.canExecute(entity)) return undefined;
    return {
      label: 'Add tag',
      icon: Tag,
      action: () => {
        setOpen(false);
        addTagAction.execute([entity]);
      },
      get hotkeyToken() {
        return activeHotkeyToken(TOKENS.entity.action.tags);
      },
      group: 'macro' as const,
    };
  };

  const copyLinkOp = (): SplitFileMenuAction | undefined => {
    const entity = menuEntity();
    // Some entities have no shareable link (a reminder resolves to no block, so
    // its URL is dead); skip the item rather than copy one that won't open.
    if (entity && !copyLinkAction.canExecute(entity)) return undefined;
    // Foreign PRs link out via their entity URL (GitHub); the block-derived
    // fallback would mint an internal /pr URL that doesn't resolve, so omit
    // the item when the entity is unavailable.
    if (!entity && blockName === 'pr') return undefined;
    return {
      label: 'Copy Link',
      icon: Link,
      action: () => {
        if (entity) {
          void copyLinkAction.execute([entity]);
        } else {
          void copyLinkAction.executeByBlock(props.id, props.entityKind);
        }
      },
      get hotkeyToken() {
        return activeHotkeyToken(TOKENS.entity.action.copyLink);
      },
      group: 'sharing' as const,
    };
  };

  const copyEntityIdOp = (): SplitFileMenuAction => ({
    label: 'Copy ID',
    icon: Copy,
    action: () => {
      void copyEntityIdAction.executeById(props.id);
    },
    get hotkeyToken() {
      return activeHotkeyToken(TOKENS.entity.action.copyEntityId);
    },
    group: 'sharing' as const,
  });

  createEffect(() => {
    const openMenu = () => setOpen(true);
    ctx.setTitleFileMenuTrigger(() => openMenu);
    onCleanup(() => ctx.setTitleFileMenuTrigger(undefined));
  });

  const ownsMenuEntity = () => props.permissions === Permissions.OWNER;

  const ops = createMemo<SplitFileMenuAction[]>(() => {
    const mapped = props.ops
      .map((op) => {
        if (isDefaultFileOperation(op)) {
          return match(op.op)
            .returnType<SplitFileMenuAction | null>()
            .with('delete', () => {
              if (!ownsMenuEntity()) return null;
              return {
                label: 'Delete',
                action: () => {
                  const entity = menuEntity();
                  if (!entity) return;
                  setOpen(false);
                  openBulkEditModal({
                    view: 'delete',
                    entities: [entity],
                    onFinish: () => {
                      toast.success('Deleted');
                      if (props.onDelete) {
                        props.onDelete();
                      } else {
                        returnSplitToRecentListView(ctx.handle);
                      }
                    },
                    onError: () => toast.failure('Failed to delete'),
                  });
                },
                icon: Trash,
                group: 'delete' as const,
              };
            })
            .with('rename', () => {
              if (!ownsMenuEntity()) return null;
              return {
                label: 'Rename',
                action: () => {
                  const entity = menuEntity();
                  if (!entity) return;
                  setOpen(false);
                  openBulkEditModal({
                    view: 'rename',
                    entities: [entity],
                    onFinish: () => toast.success('Renamed'),
                    onError: () => toast.failure('Failed to rename'),
                  });
                },
                icon: Rename,
                get hotkeyToken() {
                  return activeHotkeyToken(TOKENS.entity.action.rename);
                },
                group: 'file' as const,
              };
            })
            .with('copy', () => {
              return {
                label: 'Duplicate',
                action: async () => {
                  if (props.itemType === 'project') {
                    console.warn(
                      'Attempting to copy project!. This should not happen'
                    );
                    return;
                  }
                  const res = await itemOperations.copyItem({
                    itemType: props.itemType,
                    id: props.id,
                    name: props.name,
                  });
                  if (!res) return;
                  if (props.onDuplicate) {
                    props.onDuplicate(res);
                  } else {
                    replaceOrInsertSplit(
                      {
                        id: res,
                        type: blockName,
                      },
                      'entity-actions-menu'
                    );
                  }
                },
                icon: Copy,
                group: 'file' as const,
              };
            })
            .with('moveToProject', () => {
              if (!ownsMenuEntity()) return null;
              return {
                label: 'Move to Folder',
                action: () => {
                  const entity = buildEntityData({
                    id: props.id,
                    name: props.name,
                    blockName: props.entityKind,
                  });
                  if (!entity) return;
                  setOpen(false);
                  openBulkEditModal({
                    view: 'moveToProject',
                    entities: [entity],
                    onFinish: () => toast.success('Moved to folder'),
                    onError: () => toast.failure('Failed to move to folder'),
                  });
                },
                icon: ArrowRight,
                get hotkeyToken() {
                  return activeHotkeyToken(TOKENS.entity.action.moveToFolder);
                },
                group: 'file' as const,
              };
            })
            .exhaustive();
        } else {
          return op;
        }
      })
      .filter((op) => !!op);
    return [
      favoriteOp(),
      muteOp(),
      reminderOp(),
      addTagOp(),
      ...(props.entityKind === 'task' &&
      projectsFlag().enabled &&
      (props.permissions === Permissions.OWNER ||
        props.permissions === Permissions.CAN_EDIT)
        ? [
            {
              label: 'Add to project…',
              icon: Stack,
              group: 'file' as const,
              action: () => {
                setOpen(false);
                setAssigningProject(true);
              },
            },
          ]
        : []),
      copyLinkOp(),
      copyEntityIdOp(),
      ...mapped,
    ].filter((op) => !!op);
  });

  const filteredTools = createMemo(() =>
    (props.tools ?? []).filter((t) => !t.condition || t.condition())
  );

  const tools = createMemo<SplitFileMenuAction[]>(() =>
    filteredTools().map((tool) => ({
      label: typeof tool.label === 'function' ? tool.label() : tool.label,
      icon: tool.icon,
      children: tool.children,
      hotkeyToken: tool.hotkeyToken,
      group: tool.group,
      action: (e?: MouseEvent) => {
        tool.action();
        if (tool.focusTarget) {
          triggerFocusInput(
            tool.focusTarget,
            e?.currentTarget as HTMLElement | undefined
          );
        }
        setOpen(false);
      },
    }))
  );

  const actionGroups = createMemo<SplitFileMenuActionGroups>(() => {
    const groups: SplitFileMenuActionGroups = {
      entity: [],
      sender: [],
      sharing: [],
      macro: [],
      file: [],
      delete: [],
    };
    for (const tool of tools()) {
      groups[tool.group ?? 'entity'].push(tool);
    }
    for (const op of ops()) {
      groups[op.group ?? 'entity'].push(op);
    }
    return groups;
  });

  createEffect(() => {
    ctx.setTitleFileMenuActions(actionGroups());
  });

  onCleanup(() => ctx.setTitleFileMenuActions(undefined));

  return (
    <>
      <Show
        when={isTouchDevice()}
        fallback={
          <DesktopRender
            open={open()}
            onOpenChange={setOpen}
            triggerClass={props.buttonClass}
            groups={actionGroups()}
          />
        }
      >
        <MobileRender
          open={open()}
          onOpenChange={setOpen}
          triggerClass={props.buttonClass}
          groups={actionGroups()}
          views={props.mobileViews}
        />
      </Show>
      <Show when={projectsFlag().enabled && assigningProject()}>
        <ProjectAssignmentDialog
          taskIds={[props.id]}
          onClose={() => setAssigningProject(false)}
        />
      </Show>
    </>
  );
}

export type BlockSplitFileMenuProps = Omit<
  SplitFileMenuProps,
  'entityKind' | 'permissions'
> & { permissions?: Permissions };

/** Supplies legacy Block identity, permissions, and registered hotkeys. */
export function BlockSplitFileMenu(props: BlockSplitFileMenuProps) {
  const entityKind = useBlockAliasedName();
  const permissions = useGetPermissions();

  return (
    <SplitFileMenu
      {...props}
      entityKind={entityKind}
      permissions={props.permissions ?? permissions()}
    />
  );
}
